import React, { PropsWithChildren, useLayoutEffect, useRef } from "react";

import "./busy-blocker.css";
import { useFocusLock } from "../../hooks/useFocusLock";
import { useKeyTrap } from "../../hooks/useKeyboard";

// NOTE: React's CSSProperties type does not allow arbitrary custom properties.
// We intersect with an explicit declaration for our CSS variable instead of
// globally augmenting React types (which can cause duplication & pollution).
type BusyBlockerStyle = React.CSSProperties & { "--busy-blocker-delay"?: string };

export const BusyBlocker = ({
	isOpen,
	delayInMs: delay = 300,
}: PropsWithChildren<{
	isOpen: boolean;
	/**
	 * Delay to show the busy blocker
	 * UI will be blocked but not yet visible
	 */
	delayInMs?: number;
}>) => {
	// We want to use a transition delay but that does not work on component mount
	// therefore we set it by javascript when drawn
	// Remark: This makes it not possible to use as server component
	const elRef = useRef<HTMLDivElement>(null);

	const focusedElRef = useRef<HTMLElement | null>(null);

	useLayoutEffect(() => {
		const el = elRef.current;
		if (!el) return;

		if (isOpen) {
			// Remember focused item
			const newFocusedEl = document.activeElement;
			const htmlEl = (newFocusedEl as HTMLElement)?.focus
				? (newFocusedEl as HTMLElement)
				: null;
			if (htmlEl) focusedElRef.current = htmlEl;

			el.classList.add("busy-blocker--open");
		} else {
			el.classList.remove("busy-blocker--open");

			// Restore focus if we are still on .busy-blocker
			// If focus is changed, code explicitly moved focus and we don't restore
			if (focusedElRef.current) {
				const newFocusedEl = document.activeElement;
				const htmlEl = (newFocusedEl as HTMLElement)?.classList
					? (newFocusedEl as HTMLElement)
					: null;
				if (htmlEl?.classList.contains("busy-blocker")) {
					focusedElRef.current?.focus();
				}
			}
		}
	}, [isOpen]);

	useFocusLock(elRef, {
		isActive: isOpen,
	});

	// Prevent keystrokes bubble up to the parent elements
	useKeyTrap(elRef, {
		enabled: isOpen,
	});

	return (
		<>
			<div
				ref={elRef}
				className={"busy-blocker"}
				// Explicitly type the style object to include our custom property.
				style={{ "--busy-blocker-delay": `${delay}ms` } as BusyBlockerStyle}
				tabIndex={-1}
			>
				<div className="busy-blocker__dots">
					<div className="busy-blocker__dot" />
					<div className="busy-blocker__dot" />
					<div className="busy-blocker__dot" />
					<div className="busy-blocker__dot" />
				</div>
			</div>
		</>
	);
};

BusyBlocker.displayName = "BusyBlocker";
